import { Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getQuestionTypeLabel } from '../../../constants';
import {
    CreateFormQuestionOptionBody,
    MoveFormQuestionOptionBody,
    UpdateFormQuestionOptionBody,
} from '../../../services/apiSLTP/qhse/forms/formQuestionOptions';
import { FormQuestionWithOptions } from '../../../services/apiSLTP/qhse/forms/formQuestions';
import { capitalizeFirstLetter } from '../../../utils/capitalize';
import CustomTextInput from '../../ui/CustomTextInput';
import IconButton from '../../ui/IconButton';
import ToggleLine from '../../ui/ToggleLine';
import QuestionTypeEditor from './options/QuestionOptionsEdit';

interface QuestionEditProps {
    question: FormQuestionWithOptions;
    questionsLength: number;
    index: number;
    number: number;
    title: string;
    required: boolean;
    onTitleChange: (text: string) => void;
    onRequiredChange: (required: boolean) => void;
    onDuplicate: () => void;
    onDelete: () => void;
    onAddQuestionOption: (data: CreateFormQuestionOptionBody) => void;
    onDeleteQuestionOption: (optionId: string) => void;
    onUpdateQuestionOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onMoveQuestionOption: (optionId: string, data: MoveFormQuestionOptionBody) => void;
    onMoveQuestion: (questionId: string, toSectionId: string, order: number) => void;
}

const QuestionEdit: React.FC<QuestionEditProps> = ({
    question,
    number,
    questionsLength,
    index,
    title,
    required,
    onTitleChange,
    onRequiredChange,
    onDuplicate,
    onDelete,
    onAddQuestionOption,
    onDeleteQuestionOption,
    onUpdateQuestionOption,
    onMoveQuestionOption,
    onMoveQuestion,
}) => {
    const { t } = useTranslation();
    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Box display="flex" flexDirection="row" gap={1} width="100%">
                <Box width="20px" pt={1.7} textAlign={'end'} color="text.secondary">
                    {number}.
                </Box>
                <Box display="flex" flexDirection="column" gap={1} width="100%">
                    <CustomTextInput value={title} onChange={onTitleChange} autoFocus />
                    <QuestionTypeEditor
                        question={question}
                        onAddQuestionOption={onAddQuestionOption}
                        onDeleteQuestionOption={onDeleteQuestionOption}
                        onUpdateQuestionOption={onUpdateQuestionOption}
                        onMoveQuestionOption={onMoveQuestionOption}
                    />
                </Box>
            </Box>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems={'center'}>
                <Box sx={{ paddingLeft: 5, fontStyle: 'italic', opacity: 0.7 }}>{capitalizeFirstLetter(t(getQuestionTypeLabel(question.type)))}</Box>
                <Box display="flex" gap={2} alignItems="center">
                    <IconButton iconName="contentCopy" onClick={onDuplicate} />
                    <IconButton iconName="deleteForever" onClick={onDelete} />
                    <IconButton
                        onClick={() => onMoveQuestion(question._id, question.sectionId, question.order - 1)}
                        iconName="arrowUpward"
                        disabled={index === 0}
                    />
                    <IconButton
                        onClick={() => onMoveQuestion(question._id, question.sectionId, question.order + 1)}
                        iconName="arrowDownward"
                        disabled={index === questionsLength - 1}
                    />
                    <Divider orientation="vertical" flexItem />
                    <ToggleLine checked={required} onChange={onRequiredChange} title={capitalizeFirstLetter(t('required'))} />
                </Box>
            </Box>
        </Box>
    );
};

export default QuestionEdit;
